<template>
  <div v-if="billTemplate">
    <RightPanel
      :editDisabled="editDisabled"
      :bill="billTemplate.bill"
      :customdate="customdate"
      @updateArchived="updateBillArchived"
      @updateTracker="updateBillTracker"
      @updateTotal="updateBillCalculateTotal"
      @removeDuplicates="updateBillRemoveDuplicates"
      @updateBreakdown="updateBillBreakdown"
    />
    <Header
      :editDisabled="editDisabled"
      :customdate="customdate"
      :address="address"
      :bill="billTemplate.bill"
      @toggleLocked="toggleLocked"
      @toggleSpecial="toggleSpecial"
      @updateBillDate="updateBillDate"
      @updateBillAdjustedTime="updateBillAdjustedTime"
      @updateBillAddress="updateBillAddress"
    />
    <table class="bill">
      <tr class="billheading">
        <td colspan="5">
          {{ billTemplate.bill.heading }}
          &#160;
          <a @click="toggleBillProjection" v-if="!editDisabled">toggle</a>
        </td>
      </tr>
      <tr class="entry">
        <th>
          <span>Re:</span>
          <strong>{{ computedCategory }}</strong>
          <span>-</span>
          {{ computedSubjectProperty }}
        </th>
        <td />
        <td />
        <td />
        <td />
      </tr>
      <tr class="moniesheading">
        <th>&#160;</th>
        <td />
        <td />
        <td />
        <td />
      </tr>
      <template v-for="billTemplateMoney in computedBillTemplateMonies">
        <tr class="moniesheading" :key="`moniesheading${billTemplateMoney.id}`">
          <th>{{ billTemplateMoney.name }}</th>
          <td />
          <td />
          <td />
          <td />
        </tr>
        <template
          v-for="billTemplateSection in billTemplateMoney.templateSections"
        >
          <template v-if="!billTemplateSection.selected">
            <tr
              class="sectionheading"
              :key="`sectionheading${billTemplateSection.id}`"
            >
              <th>
                <a
                  v-if="!editDisabled"
                  @click="updateSection(billTemplateMoney, billTemplateSection)"
                  >{{ billTemplateSection.name }}</a
                >
                <span v-else>{{ billTemplateSection.name }}</span>
              </th>
              <td />
              <td />
              <td />
              <td />
            </tr>
            <template v-if="billTemplateSection.section.breakdown">
              <tr
                class="entry"
                :key="`sectionbreakdown${billTemplateSection.id}`"
              >
                <th>
                  <table>
                    <template
                      v-for="billTemplateEntry in billTemplateSection.templateEntries"
                    >
                      <tr
                        class="breakdown"
                        v-if="!billTemplateEntry.selected"
                        :key="'breakdownLine' + billTemplateEntry.id"
                      >
                        <th>
                          <EntryPrefix
                            :entryType="billTemplateEntry.entrytype"
                          />
                          <a
                            @click="
                              updateEntry(
                                billTemplateMoney,
                                billTemplateSection,
                                billTemplateEntry
                              )
                            "
                            v-if="!editDisabled"
                            >{{ billTemplateEntry.name }}</a
                          >
                          <span v-else>{{ billTemplateEntry.name }}</span>
                        </th>
                        <td>
                          {{ billTemplateEntry.entry.value | toCurrency }}
                        </td>
                      </tr>
                      <tr
                        class="breakdown"
                        v-if="billTemplateEntry.selected"
                        :key="`breakdownLineEdit${billTemplateEntry.id}`"
                      >
                        <th colspan="2">
                          <div class="ajaxForm">
                            <h2>{{ billTemplateEntry.name }}</h2>
                            <p>
                              Edit details for this entry and click "Update" to
                              save
                            </p>
                            <table class="simple">
                              <tr>
                                <th>value</th>
                                <td>
                                  <template
                                    v-if="!billTemplateEntry.entry.fundview"
                                  >
                                    <v-text-field
                                      type="number"
                                      v-model.number="billEntry.value"
                                      label="Value"
                                      v-if="!billTemplateEntry.entry.fundview"
                                    />
                                    <a @click="calculate">calculate</a>
                                  </template>
                                  <template
                                    v-if="billTemplateEntry.entry.fundview"
                                  >
                                    {{
                                      billTemplateEntry.entry.fundview.amount
                                        | toCurrency
                                    }}
                                    <v-btn
                                      @click="
                                        updateBillEntryFundviewClear(
                                          billTemplateMoney,
                                          billTemplateSection,
                                          billTemplateEntry
                                        )
                                      "
                                      >unlink</v-btn
                                    >
                                  </template>
                                </td>
                              </tr>
                            </table>
                            <v-btn
                              @click="
                                updateBillEntry(
                                  billTemplateMoney,
                                  billTemplateSection,
                                  billTemplateEntry
                                )
                              "
                              color="info"
                            >
                              <v-icon>save</v-icon>Save
                            </v-btn>
                            <hr />
                            <v-btn @click="clearSelection">
                              <v-icon>undo</v-icon>Cancel
                            </v-btn>
                          </div>
                        </th>
                      </tr>
                    </template>
                    <tr class="breakdown">
                      <th>
                        <span class="billEntryPrefix">{{
                          billTemplateSection.section.breakdown.total
                            | toCurrency
                        }}</span>
                      </th>
                      <td style="border-top: 1px solid black">
                        <span
                          :class="
                            billTemplateSection.section.breakdown.total ==
                            billTemplateSection.section.total
                              ? 'breakdownTotalGood'
                              : 'breakdownTotalBad'
                          "
                          >{{
                            billTemplateSection.section.total | toCurrency
                          }}</span
                        >
                      </td>
                    </tr>
                  </table>
                </th>
                <td />
                <td />
                <td />
                <td />
              </tr>
              <tr
                class="moniesheading"
                :key="`sectionspacer${billTemplateSection.id}`"
              >
                <th>&#160;</th>
                <td />
                <td />
                <td />
                <td />
              </tr>
            </template>
            <template v-if="!billTemplateSection.section.breakdown">
              <template
                v-for="billTemplateEntry in billTemplateSection.templateEntries"
              >
                <tr
                  class="entry"
                  v-if="!billTemplateEntry.selected"
                  :key="`entry${billTemplateEntry.id}`"
                >
                  <th>
                    <EntryPrefix :billTemplateEntry="billTemplateEntry" />
                    <a
                      @click="
                        updateEntry(
                          billTemplateMoney,
                          billTemplateSection,
                          billTemplateEntry
                        )
                      "
                      v-if="!editDisabled"
                      >{{ billTemplateEntry.name }}</a
                    >
                    <span v-if="!billTemplateEntry.entry.accounted">*</span>
                    <span
                      class="billTemplateEntrySecondary"
                      v-if="billTemplateEntry.entry.secondary"
                      >- {{ billTemplateEntry.entry.secondary.body }}</span
                    >
                  </th>
                  <td>
                    <span v-if="!billTemplateSection.nosubtotal">{{
                      billTemplateEntry.entry.valueFormatted
                    }}</span>
                  </td>
                  <td>
                    <span
                      v-if="
                        billTemplateSection.nosubtotal &&
                        billTemplateMoney.paidreceived == 0
                      "
                      >{{ billTemplateEntry.entry.valueFormatted }}</span
                    >
                  </td>
                  <td>
                    <span
                      v-if="
                        billTemplateSection.nosubtotal &&
                        billTemplateMoney.paidreceived == 1
                      "
                      >{{ billTemplateEntry.entry.valueFormatted }}</span
                    >
                  </td>
                  <td>
                    <a
                      @click="
                        toggleAccounted(
                          billTemplateMoney,
                          billTemplateSection,
                          billTemplateEntry
                        )
                      "
                      v-if="!billTemplateEntry.entry.accounted && !editDisabled"
                      small
                      >account</a
                    >
                  </td>
                </tr>
                <tr
                  v-if="billTemplateEntry.selected"
                  :key="`entryselected${billTemplateEntry.id}`"
                >
                  <td colspan="5">
                    <div class="ajaxForm">
                      <h2>{{ billTemplateEntry.name }}</h2>
                      <p>
                        Edit details for this entry and click "Update" to save
                      </p>
                      <!-- <v-form ref="entryForm" v-model="valid" :lazy-validation="true"> -->
                      <v-combobox
                        v-model="description"
                        :items="secondaries"
                        label="Secondary description"
                        no-filter
                        @input.native="
                          completeSecondary($event.srcElement.value)
                        "
                        outline
                      />
                      <v-text-field
                        type="number"
                        v-model.number="billEntry.value"
                        label="Value"
                        outline
                        v-if="!billTemplateEntry.entry.fundview"
                      />
                      <span v-if="billTemplateEntry.entry.fundview">{{
                        billTemplateEntry.entry.fundview.amount | toCurrency
                      }}</span>
                      <v-btn
                        @click="
                          updateBillEntryFundviewClear(
                            billTemplateMoney,
                            billTemplateSection,
                            billTemplateEntry
                          )
                        "
                        v-if="billTemplateEntry.entry.fundview"
                        >unlink</v-btn
                      >
                      <div>
                        <v-btn
                          @click="
                            updateBillEntry(
                              billTemplateMoney,
                              billTemplateSection,
                              billTemplateEntry
                            )
                          "
                          color="info"
                        >
                          <v-icon>save</v-icon>Save
                        </v-btn>
                        <v-btn
                          @click="
                            removeEntry(
                              billTemplateMoney,
                              billTemplateSection,
                              billTemplateEntry,
                              true
                            )
                          "
                          color="error"
                        >
                          <v-icon>delete</v-icon>Delete
                        </v-btn>
                        <v-btn @click="clearSelection">
                          <v-icon>undo</v-icon>Cancel
                        </v-btn>
                        <v-btn
                          @click="
                            toggleAccounted(
                              billTemplateMoney,
                              billTemplateSection,
                              billTemplateEntry
                            )
                          "
                        >
                          <v-icon>{{
                            billTemplateEntry.entry.accounted ? "undo" : "check"
                          }}</v-icon>
                          {{
                            billTemplateEntry.entry.accounted
                              ? "Unaccount"
                              : "Account"
                          }}
                        </v-btn>
                      </div>
                      <!-- </v-form> -->
                      <hr />
                      <table
                        v-if="
                          billTemplateMoney.paidreceived == 0 &&
                          unusedReceipts.length > 0
                        "
                      >
                        <tr>
                          <th>Fundview</th>
                          <th>Reference</th>
                          <th>Amount</th>
                        </tr>
                        <tr v-for="receipt in unusedReceipts" :key="receipt.id">
                          <td>
                            <v-btn
                              @click="
                                updateBillEntryReceipt(
                                  billTemplateMoney,
                                  billTemplateSection,
                                  billTemplateEntry,
                                  receipt
                                )
                              "
                            >
                              <v-icon>external_link</v-icon>
                              {{ receipt.id }}
                            </v-btn>
                          </td>
                          <td>{{ receipt.senderreference }}</td>
                          <td style="text-align: right">
                            {{ receipt.amount | toCurrency }}
                          </td>
                        </tr>
                      </table>
                      <table
                        v-if="
                          billTemplateMoney.paidreceived == 1 &&
                          unusedPayments.length > 0
                        "
                      >
                        <tr>
                          <th>Fundview</th>
                          <th>Beneficiary</th>
                          <th>Amount</th>
                        </tr>
                        <tr v-for="payment in unusedPayments" :key="payment.id">
                          <td>
                            <v-btn
                              @click="
                                updateBillEntryPayment(
                                  billTemplateMoney,
                                  billTemplateSection,
                                  billTemplateEntry,
                                  payment
                                )
                              "
                            >
                              <v-icon>external_link</v-icon>
                              {{ payment.id }}
                            </v-btn>
                          </td>
                          <td>{{ payment.beneficiary }}</td>
                          <td style="text-align: right">
                            {{ payment.amount | toCurrency }}
                          </td>
                        </tr>
                      </table>
                    </div>
                  </td>
                </tr>
              </template>
            </template>
          </template>
          <tr
            v-if="billTemplateSection.selected"
            :key="`section${billTemplateSection.id}`"
          >
            <td colspan="5">
              <div class="ajaxForm">
                <h1>{{ billTemplateSection.name }}</h1>
                <table>
                  <tr
                    v-if="
                      billTemplateSection.templateEntries &&
                      billTemplateSection.templateEntries.length
                    "
                  >
                    <td colspan="3">
                      <h3>Existing entries</h3>
                    </td>
                  </tr>
                  <tr
                    v-for="entryUsed in billTemplateSection.templateEntries"
                    :key="`entryUsed${entryUsed.id}`"
                  >
                    <td>
                      <EntryPrefix :billTemplateEntry="entryUsed" />
                      <a
                        @click="
                          updateEntry(
                            billTemplateMoney,
                            billTemplateSection,
                            entryUsed
                          )
                        "
                        >{{ entryUsed.name }}</a
                      >
                    </td>
                    <td>{{ entryUsed.entry.valueFormatted }}</td>
                    <td>
                      <v-btn
                        @click="
                          removeEntry(
                            billTemplateMoney,
                            billTemplateSection,
                            entryUsed,
                            false
                          )
                        "
                        color="error"
                        small
                      >
                        <v-icon>delete</v-icon>Delete
                      </v-btn>
                    </td>
                  </tr>
                  <tr
                    v-if="
                      billTemplateSection.templateEntriesUnused &&
                      billTemplateSection.templateEntriesUnused.length
                    "
                  >
                    <td colspan="3">
                      <h3>Choose the entries to include in this section</h3>
                    </td>
                  </tr>
                  <tr
                    v-for="billTemplateEntryUnused in billTemplateSection.templateEntriesUnused"
                    :key="`billTemplateEntryUnused${billTemplateEntryUnused.id}`"
                  >
                    <td colspan="2">
                      <EntryPrefix
                        :billTemplateEntry="billTemplateEntryUnused"
                      />
                      <span>{{ billTemplateEntryUnused.name }}</span>
                    </td>
                    <td>
                      <v-btn
                        @click="
                          createBillEntry(
                            billTemplateMoney,
                            billTemplateSection,
                            billTemplateEntryUnused
                          )
                        "
                        color="info"
                        small
                      >
                        Add
                        <v-icon>chevron_right</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </table>
                <hr />
                <v-btn @click="clearSelection">Cancel</v-btn>
              </div>
            </td>
          </tr>
          <tr
            class="sectiontotal"
            v-if="
              !billTemplateSection.section.breakdown &&
              !billTemplateSection.nosubtotal
            "
            :key="`sectiontotal${billTemplateSection.id}`"
          >
            <th>{{ billTemplateSection.label }}</th>
            <td style="border-top: 1px solid black">
              {{ billTemplateSection.section.total | toCurrency }}
            </td>
            <td
              style="border-top: 1px solid black"
              v-if="billTemplateMoney.paidreceived == 1"
            />
            <td style="border-top: 1px solid black">
              {{ billTemplateSection.section.total | toCurrency }}
            </td>
            <td v-if="billTemplateMoney.paidreceived == 0" />
            <td />
          </tr>
        </template>
        <tr
          class="entry"
          v-if="billTemplateMoney.money && billTemplateMoney.money.vat != 0"
          :key="`moniesvat${billTemplateMoney.id}`"
        >
          <th>VAT on chargeable items at {{ billTemplate.bill.vatrate }}%</th>
          <td />
          <td v-if="billTemplateMoney.paidreceived == 1" />
          <td>{{ billTemplateMoney.money.vat | toCurrency }}</td>
          <td v-if="billTemplateMoney.paidreceived == 0" />
          <td />
        </tr>
        <tr
          class="moniestotal"
          v-if="billTemplateMoney.money"
          :key="`moniestotal${billTemplateMoney.id}`"
        >
          <th />
          <td />
          <td v-if="billTemplateMoney.paidreceived == 1" />
          <td style="border-top: 1px solid black">
            {{ billTemplateMoney.money.total | toCurrency }}
          </td>
          <td
            style="border-top: 1px solid black"
            v-if="billTemplateMoney.paidreceived == 0"
          />
          <td style="border-top: 1px solid black">
            {{ billTemplateMoney.money.total | toCurrency }}
          </td>
        </tr>
      </template>
      <tr>
        <td style="font-size: 0.8em">
          <span v-if="billTemplate.bill.unaccounted"
            >* indicates items which have not yet been accounted</span
          >
        </td>
        <td colspan="3" style="border-top: 1px solid black">
          <strong>BALANCE</strong>
          {{ billTemplate.bill.duetag }}
        </td>
        <td
          style="
            border-top: 1px solid black;
            border-bottom: 3px solid black;
            text-align: right;
          "
        >
          {{ billTemplate.bill.total | toCurrency }}
        </td>
      </tr>
      <tr>
        <th>&#160;</th>
        <td />
        <td />
        <td />
        <td />
      </tr>
      <tr>
        <td colspan="5">
          <template v-if="!editPayable">
            <div v-if="billTemplate.bill.payable">
              Payable to {{ billTemplate.bill.payable }} as instructed
            </div>
            <div>
              <v-btn @click="editPayable = true">edit payable</v-btn>
            </div>
          </template>
          <div class="ajaxForm" v-if="editPayable">
            <p>
              Enter below specifically who this bill is payable to in the event
              it is different to the addressee
            </p>
            <v-text-field label="Payable" v-model="payable"></v-text-field>
            <v-btn @click="updatePayable">Update</v-btn>
            <v-btn @click="editPayable = false">Cancel</v-btn>
          </div>
        </td>
      </tr>
    </table>
    <pre v-if="calculateResult">{{ calculateResult }}</pre>
  </div>
</template>

<script>
import BILL_TEMPLATE_POPULATED from "@/graphql/accounts/billTemplate/BillTemplatePopulated";
import CREATE_BILL_ENTRY from "@/graphql/accounts/bill/CreateBillEntry";
import DELETE_BILL_ENTRY from "@/graphql/accounts/bill/DeleteBillEntry";
import SECONDARIES from "@/graphql/accounts/bill/Secondaries";
import UPDATE_BILL_ADDRESS from "@/graphql/accounts/bill/UpdateBillAddress";
import UPDATE_BILL_ADJUSTED_TIME from "@/graphql/accounts/bill/UpdateBillAdjustedTime";
import UPDATE_BILL_ARCHIVED from "@/graphql/accounts/bill/UpdateBillArchived";
import UPDATE_BILL_BILLPROJECTION from "@/graphql/accounts/bill/UpdateBillBillProjection";
import UPDATE_BILL_BREAKDOWN from "@/graphql/accounts/bill/UpdateBillBreakdown";
import UPDATE_BILL_CALCULATE_TOTAL from "@/graphql/accounts/bill/UpdateBillCalculateTotal";
import UPDATE_BILL_DATE from "@/graphql/accounts/bill/UpdateBillDate";
import UPDATE_BILL_LOCKED from "@/graphql/accounts/bill/UpdateBillLocked";
import UPDATE_BILL_PAYABLE from "@/graphql/accounts/bill/UpdateBillPayable";
import UPDATE_BILL_REMOVE_DUPLICATES from "@/graphql/accounts/bill/UpdateBillRemoveDuplicates";
import UPDATE_BILL_SPECIAL from "@/graphql/accounts/bill/UpdateBillSpecial";
import UPDATE_BILL_TRACKER from "@/graphql/accounts/bill/UpdateBillTracker";
import UPDATE_BILLENTRY from "@/graphql/accounts/bill/UpdateBillEntry";
import UPDATE_BILLENTRY_ACCOUNTED from "@/graphql/accounts/bill/UpdateBillEntryAccounted";
import UPDATE_BILLENTRY_FUNDVIEW_CLEAR from "@/graphql/accounts/bill/UpdateBillEntryFundviewClear";
import UPDATE_BILLENTRY_PAYMENT from "@/graphql/accounts/bill/UpdateBillEntryPayment";
import UPDATE_BILLENTRY_RECEIPT from "@/graphql/accounts/bill/UpdateBillEntryReceipt";
import EntryPrefix from "@/components/bill/EntryPrefix";
import Header from "@/components/bill/Header";
import RightPanel from "@/components/bill/RightPanel";
import nbmlaw from "@/api/nbmlaw";
export default {
  name: "Bill",
  metaInfo() {
    return {
      title: "Bill",
      titleTemplate: "%s - Accounts - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  apollo: {
    billTemplate: {
      query: BILL_TEMPLATE_POPULATED,
      variables() {
        return {
          id: Number(this.$route.params.id)
        };
      },
      update: (data) => data.billTemplatePopulated,
      fetchPolicy: "cache-and-network"
    }
  },
  components: { EntryPrefix, Header, RightPanel },
  computed: {
    computedBillTemplateMonies() {
      if (!this.billTemplate) {
        return undefined;
      }
      return this.billTemplate.templateMonies.map((tm) => {
        return {
          ...tm,
          templateSections: tm.templateSections
            .filter((ts) => ts.section)
            .map((ts) => {
              return {
                ...ts,
                templateEntries: ts.templateEntries
                  .filter((te) => te.entry)
                  .map((te) => {
                    return {
                      ...te,
                      selected:
                        te.entry &&
                        te.entry.value != null &&
                        this.billTemplateEntrySelected &&
                        this.billTemplateEntrySelected.id == te.id
                    };
                  }),
                templateEntriesUnused: ts.templateEntries
                  .filter((te) => !te.entry)
                  .sort(function (a, b) {
                    return a.name.localeCompare(b.name);
                  }),
                selected:
                  this.billTemplateSectionSelected &&
                  !this.billTemplateEntrySelected &&
                  ts.id == this.billTemplateSectionSelected.id
                    ? true
                    : false
              };
            })
        };
      });
    },
    computedCategory() {
      if (
        !this.billTemplate ||
        !this.billTemplate.bill ||
        !this.billTemplate.bill.file ||
        !this.billTemplate.bill.file.category ||
        !this.billTemplate.bill.file.category.shortname
      ) {
        return "unknown";
      }
      return this.billTemplate.bill.file.category.shortname;
    },
    computedSubjectProperty() {
      if (
        !this.billTemplate ||
        !this.billTemplate.bill ||
        !this.billTemplate.bill.file ||
        !this.billTemplate.bill.file.subjectProperty
      ) {
        return "tba";
      }
      return this.billTemplate.bill.file.subjectProperty;
    }
  },
  data() {
    return {
      editDisabled: false,
      calculateResult: null,
      address: null,
      payable: null,
      editPayable: false,
      sectionEntries: null,
      billEntry: null,
      valid: false,
      timerId: null,
      unusedPayments: [],
      unusedReceipts: [],
      billTemplateMoneySelected: null,
      billTemplateSectionSelected: null,
      billTemplateEntrySelected: null,
      customdate: null,
      secondaries: [],
      description: ""
    };
  },
  methods: {
    async fetchUnusedPayments() {
      const response = await nbmlaw.get(
        `/cors/admin/bill/bills/${this.$route.params.id}/unusedPayments`
      );
      // console.log(response.data)
      this.unusedPayments = response.data;
    },
    async fetchUnusedReceipts() {
      const response = await nbmlaw.get(
        `/cors/admin/bill/bills/${this.$route.params.id}/unusedReceipts`
      );
      // console.log(response.data)
      this.unusedReceipts = response.data;
    },
    async fetchSecondaries(value) {
      if (!value) {
        this.secondaries = [];
        return;
      }
      const response = await this.$apollo.query({
        query: SECONDARIES,
        variables: { value, limit: 10 }
      });
      if (!response || !response.data || !response.data.secondaries) {
        return;
      }
      this.secondaries = response.data.secondaries.map((item) => item.body);
    },
    async toggleBillProjection() {
      await this.$apollo.mutate({
        mutation: UPDATE_BILL_BILLPROJECTION,
        variables: {
          id: Number(this.$route.params.id)
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    async updatePayable() {
      await this.$apollo.mutate({
        mutation: UPDATE_BILL_PAYABLE,
        variables: {
          id: Number(this.$route.params.id),
          value: this.payable
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
      this.editPayable = false;
    },
    updateSection(billTemplateMoney, billTemplateSection) {
      this.clearSelection();
      this.makeSelection(billTemplateMoney, billTemplateSection, null);
    },
    makeSelection(billTemplateMoney, billTemplateSection, billTemplateEntry) {
      this.billTemplateMoneySelected = billTemplateMoney;
      this.billTemplateSectionSelected = billTemplateSection;
      this.billTemplateEntrySelected = billTemplateEntry;
    },
    clearSelection() {
      this.billTemplateMoneySelected = null;
      this.billTemplateSectionSelected = null;
      this.billTemplateEntrySelected = null;
    },
    async deleteBillEntry(
      billTemplateMoney,
      billTemplateSection,
      billTemplateEntry
    ) {
      await this.$apollo.mutate({
        mutation: DELETE_BILL_ENTRY,
        variables: {
          id: Number(this.$route.params.id),
          moneyid: billTemplateMoney.money.id,
          sectionid: billTemplateSection.section.id,
          entryid: billTemplateEntry.entry.id
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    async createBillEntry(
      billTemplateMoney,
      billTemplateSection,
      billTemplateEntry
    ) {
      await this.$apollo.mutate({
        mutation: CREATE_BILL_ENTRY,
        variables: {
          id: Number(this.$route.params.id),
          moneyid: billTemplateMoney.money.id,
          sectionid: billTemplateSection.section.id,
          btentryid: billTemplateEntry.id
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    updateEntry(billTemplateMoney, billTemplateSection, billTemplateEntry) {
      this.clearSelection();
      this.fetchUnusedPayments();
      this.fetchUnusedReceipts();
      this.makeSelection(
        billTemplateMoney,
        billTemplateSection,
        billTemplateEntry
      );
      this.description = billTemplateEntry.entry.secondary
        ? billTemplateEntry.entry.secondary.body
        : null;
      this.billEntry = {
        id: billTemplateEntry.entry.id,
        value: billTemplateEntry.entry.value
      };
    },
    completeSecondary(value) {
      // debounce
      clearTimeout(this.timerId);
      this.timerId = setTimeout(() => {
        // console.log(value)
        this.description = value;
        this.fetchSecondaries(value);
      }, 500);
    },
    async toggleAccounted(
      billTemplateMoney,
      billTemplateSection,
      billTemplateEntry
    ) {
      await this.$apollo.mutate({
        mutation: UPDATE_BILLENTRY_ACCOUNTED,
        variables: {
          id: Number(this.$route.params.id),
          moneyid: billTemplateMoney.money.id,
          sectionid: billTemplateSection.section.id,
          entryid: billTemplateEntry.entry.id
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    async updateBillEntry(
      billTemplateMoney,
      billTemplateSection,
      billTemplateEntry
    ) {
      await this.$apollo.mutate({
        mutation: UPDATE_BILLENTRY,
        variables: {
          id: Number(this.$route.params.id),
          moneyid: billTemplateMoney.money.id,
          sectionid: billTemplateSection.section.id,
          entryid: billTemplateEntry.entry.id,
          description: this.description,
          value: this.billEntry.value
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
      this.clearSelection();
      // }
    },
    removeEntry(
      billTemplateMoney,
      billTemplateSection,
      billTemplateEntry,
      clear
    ) {
      if (confirm("Are you sure?")) {
        this.deleteBillEntry(
          billTemplateMoney,
          billTemplateSection,
          billTemplateEntry
        );
        if (clear) {
          this.clearSelection();
        }
      }
    },
    async updateBillEntryReceipt(
      billTemplateMoney,
      billTemplateSection,
      billTemplateEntry,
      receipt
    ) {
      await this.$apollo.mutate({
        mutation: UPDATE_BILLENTRY_RECEIPT,
        variables: {
          id: Number(this.$route.params.id),
          moneyid: billTemplateMoney.money.id,
          sectionid: billTemplateSection.section.id,
          entryid: billTemplateEntry.entry.id,
          fundviewid: receipt.id
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
      this.fetchUnusedReceipts();
    },
    async updateBillEntryPayment(
      billTemplateMoney,
      billTemplateSection,
      billTemplateEntry,
      payment
    ) {
      await this.$apollo.mutate({
        mutation: UPDATE_BILLENTRY_PAYMENT,
        variables: {
          id: Number(this.$route.params.id),
          moneyid: billTemplateMoney.money.id,
          sectionid: billTemplateSection.section.id,
          entryid: billTemplateEntry.entry.id,
          fundviewid: payment.id
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
      this.fetchUnusedPayments();
    },
    async updateBillEntryFundviewClear(
      billTemplateMoney,
      billTemplateSection,
      billTemplateEntry
    ) {
      await this.$apollo.mutate({
        mutation: UPDATE_BILLENTRY_FUNDVIEW_CLEAR,
        variables: {
          id: Number(this.$route.params.id),
          moneyid: billTemplateMoney.money.id,
          sectionid: billTemplateSection.section.id,
          entryid: billTemplateEntry.entry.id
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
      this.fetchUnusedPayments();
      this.fetchUnusedReceipts();
    },
    async toggleLocked() {
      await this.$apollo.mutate({
        mutation: UPDATE_BILL_LOCKED,
        variables: {
          id: Number(this.$route.params.id)
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    async toggleSpecial() {
      await this.$apollo.mutate({
        mutation: UPDATE_BILL_SPECIAL,
        variables: {
          id: Number(this.$route.params.id)
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    async updateBillDate(args) {
      await this.$apollo.mutate({
        mutation: UPDATE_BILL_DATE,
        variables: {
          id: Number(this.$route.params.id),
          customdate: args.customdate
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    async updateBillAdjustedTime() {
      await this.$apollo.mutate({
        mutation: UPDATE_BILL_ADJUSTED_TIME,
        variables: {
          id: Number(this.$route.params.id)
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    async updateBillAddress(args) {
      await this.$apollo.mutate({
        mutation: UPDATE_BILL_ADDRESS,
        variables: {
          id: Number(this.$route.params.id),
          customaddress: args.customaddress
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    async updateBillArchived() {
      await this.$apollo.mutate({
        mutation: UPDATE_BILL_ARCHIVED,
        variables: {
          id: Number(this.$route.params.id)
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    async updateBillTracker() {
      await this.$apollo.mutate({
        mutation: UPDATE_BILL_TRACKER,
        variables: {
          id: Number(this.$route.params.id)
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    async updateBillCalculateTotal() {
      const data = await this.$apollo.mutate({
        mutation: UPDATE_BILL_CALCULATE_TOTAL,
        variables: {
          id: Number(this.$route.params.id)
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
      this.calculateResult = data.data.updateBillCalculateTotal;
    },
    async updateBillRemoveDuplicates() {
      await this.$apollo.mutate({
        mutation: UPDATE_BILL_REMOVE_DUPLICATES,
        variables: {
          id: Number(this.$route.params.id)
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    async updateBillBreakdown() {
      await this.$apollo.mutate({
        mutation: UPDATE_BILL_BREAKDOWN,
        variables: {
          id: Number(this.$route.params.id)
        },
        refetchQueries: [
          {
            query: BILL_TEMPLATE_POPULATED,
            variables: { id: Number(this.$route.params.id) }
          }
        ]
      });
    },
    calculate() {
      // console.log(this.billTemplateSectionSelected);
      let tally = this.billTemplateSectionSelected.section.breakdown.total;
      for (const billTemplateEntry of this.billTemplateSectionSelected
        .templateEntries) {
        if (billTemplateEntry.entry.id != this.billEntry.id) {
          tally = tally - billTemplateEntry.entry.value;
        }
      }
      this.billEntry.value = tally;
    }
  },
  watch: {
    billTemplate: {
      handler() {
        this.payable = this.billTemplate.bill
          ? this.billTemplate.bill.payable
          : undefined;
        this.customdate =
          this.billTemplate.bill && this.billTemplate.bill.customdate
            ? this.billTemplate.bill.customdate
            : this.billTemplate.bill.timestamp;
        this.address = this.billTemplate.bill.address;
      }
    }
  }
};
</script>

<style>
table.bill {
  border-collapse: collapse;
  border-spacing: 0px;
  /* border-collapse: separate;
	border-spacing: 5px;*/
}

tr.billheading td {
  text-align: center;
  font-weight: bold;
  border-bottom: 1px solid black;
}

tr.moniesheading th {
  text-align: left;
  border-right: 1px solid black;
}

tr.moniesheading td {
  border-right: 1px solid black;
  text-align: right;
}

tr.moniestotal th {
  font-weight: normal;
  text-align: right;
  border-right: 1px solid black;
}

tr.moniestotal td {
  border-right: 1px solid black;
  text-align: right;
}

tr.sectionheading th {
  font-weight: bold;
  text-align: left;
  border-right: 1px solid black;
}

tr.sectionheading td {
  border-right: 1px solid black;
  text-align: right;
}

tr.sectiontotal th {
  font-weight: normal;
  text-align: right;
  border-right: 1px solid black;
}

tr.sectiontotal td {
  border-right: 1px solid black;
  text-align: right;
}

tr.entry th {
  padding-left: 10px;
  font-weight: normal;
  text-align: left;
  border-right: 1px solid black;
}

tr.entry td {
  border-right: 1px solid black;
  text-align: right;
}

tr.breakdown th {
  font-weight: normal;
  border-right: 0px;
  text-align: left;
  padding-left: 0px;
}

tr.breakdown td {
  border: 0px;
  text-align: right;
}

div.billmonies {
  background: #aaaaaa;
  border-top: 1px solid gray;
  padding: 2px;
  font-weight: bold;
}

div.billsectionenabled {
  background: #cccccc;
  border-top: 1px solid gray;
}

div.billsectiondisabled {
  border-top: 1px solid gray;
  color: #bbbbbb;
}

div.billtotal {
  float: right;
}

div.billentryenabled {
  border-top: 1px solid gray;
  background: #eeeeee;
  color: #000000;
}

div.billentrydisabled {
  border-top: 1px solid gray;
  color: #bbbbbb;
}

div.billlink {
  float: right;
  font-size: 7pt;
  margin-left: 5px;
}

div.belabel {
  margin-left: 20px;
  padding: 5px;
}

input.billvalue {
  width: 60px;
  text-align: right;
  border: 1px solid gray;
  font-size: 7pt;
}

input.billsecondary {
  margin: 0px 5px 0px 0px;
  border: 1px solid gray;
  font-size: 7pt;
}

.ledgerYear {
  text-align: center;
  font-weight: bold;
}

.ledgerDecimal {
  text-align: right;
}

.salesDescription {
  background: gray;
  color: white;
}

.billTemplateEntryArchived {
  text-decoration: line-through;
  font-size: 0.7em;
}

.billTemplateEntryCount {
  color: gray;
}

.billTemplateEntrySecondary {
  font-size: 0.8em;
}

.billTemplateAdd {
  color: green;
}

.billTemplateLess {
  color: red;
}

.billEntryPrefix {
  color: gray;
}

.sectionEditorOdd {
  background: white;
}

/* .sectionEditorEven {
} */

.breakdownTotalBad {
  color: red;
}

.breakdownTotalGood {
  color: green;
}

.messageError {
  color: red;
}
</style>
